import { RefObject, useEffect, useRef } from 'react';
import { Outlet, useOutletContext, useParams } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@store';
import {
  FilterTag,
  IconLeft,
  IconRight,
  PermissionEnum,
  SchemaFilterType,
} from '@ui/src';
import { OrganizationType } from '../types';
import useDocuments from './hooks/use-documents';
import useExpiredDocuments from './hooks/use-expired-documents';
import DocumentsPopups from './parts/popup';
import {
  onSelectDocument,
  onSelectDocumentById,
  setBlockView,
} from '../store/slices/documents/documents';
import DocumentsCategoriesTabs from '../../../components/common/documents/parts/categories-tabs';
import DocumentsTop from './parts/documents-top';
import Agreements from './parts/agreements';
import DocumentsContent from '../../../components/common/documents/parts/documents-content';
import ExpiredDocuments from './parts/expired-documents';

import cn from './styles.module.css';

const CustomerDocuments = ({ type }: { type: OrganizationType }) => {
  const scrollWrapper = useRef<HTMLDivElement | null>(null);

  const { wrapperRef } = useOutletContext<{
    wrapperRef: RefObject<HTMLDivElement | null>;
  }>();

  const {
    stats,
    allowedCategories,
    allDocuments,
    documentsByCategoryId,
    allDocumentsSelected,
    allDocumentsTotal,
    documentsByCategoryTotal,
    selectedDocumentsByGroupId,
    needToReloadCategoryId,
    cursor,
    cursorByCategoryId,
    expiredDocumentsPagination,
    expiredDocuments,
  } = useAppSelector((state) => state.customerDocuments);
  const { activePermissions } = useAppSelector((state) => state.root);
  const { baseInformation: corporateBaseInformation } = useAppSelector(
    (state) => state.corporateCustomers.info.general,
  );
  const { baseInformation: individualBaseInformation } = useAppSelector(
    (state) => state.individualCustomers.info.general,
  );

  const { customerId } = useParams();
  const dispatch = useAppDispatch();

  const {
    selectedCategory,
    selectedDocumentsCountByCategory,
    setDeletePopupVisible,
    deleteInProgress,
    deletePopupVisible,
    onDelete,
    setRenamePopupVisible,
    setSelectedDocumentToRename,
    setDocumentName,
    setNameError,
    onRename,
    documentRenameInProgress,
    renamePopupVisible,
    documentName,
    selectedDocumentToRename,
    nameError,
    errors,
    expiredDocumentsVisible,
    setExpiredDocumentsVisible,
    search,
    setMenuVisible,
    menuVisible,
    moveInProgress,
    changeCustomerDocumentsCategoryAction,
    otherActionsMenuVisible,
    setOtherActionsMenuVisible,
    statusChangeInProgress,
    setSelectedStatus,
    changeCustomerDocumentsStatusAction,
    selectedStatus,
    getFiles,
    downloadInProgress,
    isFetching,
    searchText,
    activeLabel,
    onSearch,
    setActiveLabel,
    withLeftButton,
    onScrollButton,
    onCategoryClick,
    prevSearchText,
    prevStatuses,
    selectedStatuses,
    withScrollButton,
    onGroupSelect,
    onGroupByIdSelect,
    setSelectedStatuses,
    sort,
    onChangeSort,
    sortProp,
    isLoading,
    documentsIsFetching,
    onSetPage,
    deleteDocumentIsSuccess,
    setSubMenuVisible,
    getFile,
    subMenuVisible,
    onSelectStatuses,
    setSelectedSubMenuDocumentId,
    selectedSubMenuDocumentId,
  } = useDocuments({
    customerId,
    scrollWrapper,
    allDocuments,
    documentsByCategoryId,
    cursorByCategoryId,
    cursor,
    needToReloadCategoryId,
    allowedCategories,
    wrapperRef,
    activePermissions,
    type,
    selectedDocumentsByGroupId,
  });

  const { getListAction, filters, expiredSort } = useExpiredDocuments({
    customerId,
    setDeletePopupVisible,
    expiredDocuments,
    activePermissions,
    type,
    setRenamePopupVisible,
    setSelectedDocumentToRename,
    setExpiredDocumentsVisible,
    setSubMenuVisible: () => {},
    setSelectedSubMenuDocumentId: () => {},
    getFile: () => {},
    deleteDocumentIsSuccess,
  });

  useEffect(() => {
    if (
      localStorage.getItem('blockView') &&
      localStorage.getItem('blockView') === 'true'
    ) {
      dispatch(setBlockView(true));
    }
  }, [dispatch, localStorage]);

  useEffect(() => {
    let title: string;

    const companyName = localStorage.getItem('companyName');

    if (type === OrganizationType.INDIVIDUAL) {
      if (
        individualBaseInformation?.firstName ||
        individualBaseInformation?.lastName
      ) {
        title = `${individualBaseInformation?.firstName} ${individualBaseInformation?.lastName}: Documents`;
      } else {
        title = 'Documents';
      }
    } else if (corporateBaseInformation?.name) {
      title = `${corporateBaseInformation?.name}: Documents`;
    } else {
      title = 'Documents';
    }

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, [
    individualBaseInformation?.firstName,
    individualBaseInformation?.lastName,
    corporateBaseInformation?.name,
  ]);

  let content = <div />;

  if (
    (type === OrganizationType.INDIVIDUAL &&
      activePermissions?.includes(
        PermissionEnum.INDIVIDUAL_CUSTOMERS_DOCUMENTS_LIST,
      )) ||
    (type === OrganizationType.CORPORATE &&
      activePermissions?.includes(
        PermissionEnum.CORPORATE_CUSTOMERS_DOCUMENTS_LIST,
      ))
  ) {
    const currentCategoryCursor =
      selectedCategory && selectedCategory?.id === 'all'
        ? cursor
        : cursorByCategoryId &&
          selectedCategory &&
          cursorByCategoryId[selectedCategory.id];

    const currentCategoryDocuments =
      selectedCategory && selectedCategory?.id === 'all'
        ? allDocuments?.all
        : documentsByCategoryId &&
          selectedCategory &&
          documentsByCategoryId[selectedCategory.id];

    const currentCategoryDocumentsValues = currentCategoryCursor
      ? currentCategoryDocuments?.find(
          (item) =>
            item.startCursor === currentCategoryCursor?.startCursor &&
            item.endCursor === currentCategoryCursor?.endCursor,
        )
      : undefined;

    const organizationStatus =
      type === OrganizationType.INDIVIDUAL
        ? individualBaseInformation?.status
        : corporateBaseInformation?.status;

    content = (
      <>
        {!expiredDocumentsVisible && (
          <>
            <DocumentsTop
              expiredDocuments={expiredDocuments}
              expiredDocumentsPagination={expiredDocumentsPagination}
              setExpiredDocumentsVisible={setExpiredDocumentsVisible}
              type={type}
              activePermissions={activePermissions}
              organizationStatus={organizationStatus}
              customerId={customerId}
              search={search}
              allDocumentsSelected={allDocumentsSelected}
              selectedDocumentsCountByCategory={
                selectedDocumentsCountByCategory
              }
              setMenuVisible={setMenuVisible}
              menuVisible={menuVisible}
              moveInProgress={moveInProgress}
              selectedCategory={selectedCategory}
              allowedCategories={allowedCategories}
              documentsByCategoryId={documentsByCategoryId}
              allDocuments={allDocuments}
              changeCustomerDocumentsCategoryAction={
                changeCustomerDocumentsCategoryAction
              }
              otherActionsMenuVisible={otherActionsMenuVisible}
              setOtherActionsMenuVisible={setOtherActionsMenuVisible}
              statusChangeInProgress={statusChangeInProgress}
              setSelectedStatus={setSelectedStatus}
              changeCustomerDocumentsStatusAction={
                changeCustomerDocumentsStatusAction
              }
              selectedStatus={selectedStatus}
              setDeletePopupVisible={setDeletePopupVisible}
              getFiles={getFiles}
              downloadInProgress={downloadInProgress}
              isFetching={isFetching}
              searchText={searchText}
              activeLabel={activeLabel}
              onSearch={onSearch}
              setActiveLabel={setActiveLabel}
            />
            <div className={cn.agreementsWrapper}>
              <Agreements customerId={customerId} type={type} />
            </div>
            <DocumentsCategoriesTabs
              withLeftButton={withLeftButton}
              onScrollButton={onScrollButton}
              scrollWrapper={scrollWrapper}
              selectedCategory={selectedCategory}
              onCategoryClick={onCategoryClick}
              prevSearchText={prevSearchText}
              searchText={searchText}
              prevStatuses={prevStatuses}
              selectedStatuses={selectedStatuses}
              allowedCategories={allowedCategories}
              stats={stats}
              withScrollButton={withScrollButton}
            />
            <div className={cn.filtersWrapper}>
              {selectedStatuses?.length > 0 &&
                selectedStatuses?.map((item) => (
                  <FilterTag
                    key={item}
                    type="round"
                    title={
                      <>
                        <span className={cn.filterLabel}>{`Status: `}</span>
                        <span>{`${item.charAt(0)}${item
                          .slice(1)
                          .toLowerCase()}`}</span>
                      </>
                    }
                    className={cn.filterTag}
                    onClick={() => {
                      onSelectStatuses(
                        selectedStatuses?.filter((status) => status !== item),
                      );
                    }}
                  />
                ))}
            </div>
            <DocumentsContent
              selectedCategory={selectedCategory}
              onGroupSelect={onGroupSelect}
              onGroupByIdSelect={onGroupByIdSelect}
              currentCategoryDocuments={
                currentCategoryDocumentsValues?.documents || []
              }
              status={organizationStatus}
              allDocumentsSelected={allDocumentsSelected}
              selectedDocumentsCategoryId={
                selectedCategory
                  ? selectedDocumentsByGroupId[selectedCategory.id]
                  : 0
              }
              allDocumentsTotal={allDocumentsTotal}
              documentsByCategoryTotal={
                selectedCategory
                  ? documentsByCategoryTotal[selectedCategory?.id]
                  : 0
              }
              selectedStatuses={selectedStatuses}
              setSelectedStatuses={setSelectedStatuses}
              sort={sort}
              onChangeSort={onChangeSort}
              sortProp={sortProp}
              onSelect={(documentType, selected, id) => {
                if (
                  currentCategoryDocumentsValues?.documents?.find(
                    (item) => item.category,
                  )
                ) {
                  dispatch(
                    onSelectDocument({
                      type: documentType,
                      selected,
                      id,
                    }),
                  );
                } else {
                  dispatch(
                    onSelectDocumentById({
                      documentCategoryId: documentType,
                      selected,
                      id,
                    }),
                  );
                }
              }}
              customerId={customerId as string}
              customerType={type}
              setRenamePopupVisible={setRenamePopupVisible}
              setSelectedDocumentToRename={setSelectedDocumentToRename}
              updateExpiredDocumentsState={() =>
                getListAction({
                  filters: {
                    ...filters,
                    take: {
                      value: ['20'],
                      type: SchemaFilterType.STRING,
                    },
                    status: {
                      value: ['EXPIRED'],
                      type: SchemaFilterType.STRING,
                    },
                  },
                  sort: expiredSort,
                })
              }
              isLoading={isLoading || documentsIsFetching}
              documentsIsFetching={documentsIsFetching}
            />
            <div className={cn.documentsPagination}>
              <button
                type="button"
                className={`${cn.paginationButton} ${cn.buttonPrev} ${
                  !currentCategoryDocumentsValues?.hasPrev ||
                  isLoading ||
                  documentsIsFetching
                    ? cn.disabled
                    : ''
                }`}
                onClick={() => onSetPage('prev')}
                disabled={
                  !currentCategoryDocumentsValues?.hasPrev ||
                  isLoading ||
                  documentsIsFetching
                }
              >
                <IconLeft /> Prev
              </button>
              <button
                type="button"
                className={`${cn.paginationButton} ${cn.buttonNext} ${
                  !currentCategoryDocumentsValues?.hasNext ||
                  isLoading ||
                  documentsIsFetching
                    ? cn.disabled
                    : ''
                }`}
                onClick={() => onSetPage('next')}
                disabled={
                  !currentCategoryDocumentsValues?.hasNext ||
                  isLoading ||
                  documentsIsFetching
                }
              >
                Next <IconRight />
              </button>
            </div>
          </>
        )}
        {expiredDocumentsVisible && (
          <ExpiredDocuments
            type={type}
            customerId={customerId}
            activePermissions={activePermissions}
            setDeletePopupVisible={setDeletePopupVisible}
            deleteDocumentIsSuccess={deleteDocumentIsSuccess}
            setSubMenuVisible={setSubMenuVisible}
            setSelectedSubMenuDocumentId={setSelectedSubMenuDocumentId}
            setRenamePopupVisible={setRenamePopupVisible}
            setSelectedDocumentToRename={setSelectedDocumentToRename}
            getFile={getFile}
            subMenuVisible={subMenuVisible}
            selectedSubMenuDocumentId={selectedSubMenuDocumentId}
            setExpiredDocumentsVisible={setExpiredDocumentsVisible}
          />
        )}
        <Outlet />
      </>
    );
  }

  return (
    <div
      className={`${cn.customerDocumentsWrapper} ${
        (type === OrganizationType.INDIVIDUAL &&
          !activePermissions?.includes(
            PermissionEnum.INDIVIDUAL_CUSTOMERS_DOCUMENTS_LIST,
          )) ||
        (type === OrganizationType.CORPORATE &&
          !activePermissions?.includes(
            PermissionEnum.CORPORATE_CUSTOMERS_DOCUMENTS_LIST,
          ))
          ? cn.flexBox
          : ''
      }`}
    >
      <DocumentsPopups
        selectedCategory={selectedCategory}
        allDocumentsSelected={allDocumentsSelected}
        selectedDocumentsCountByCategory={selectedDocumentsCountByCategory}
        setDeletePopupVisible={setDeletePopupVisible}
        documentsByCategoryId={documentsByCategoryId}
        allowedCategories={allowedCategories}
        allDocuments={allDocuments}
        deleteInProgress={deleteInProgress}
        deletePopupVisible={deletePopupVisible}
        onDelete={onDelete}
        setRenamePopupVisible={setRenamePopupVisible}
        setSelectedDocumentToRename={setSelectedDocumentToRename}
        setDocumentName={setDocumentName}
        setNameError={setNameError}
        onRename={onRename}
        documentRenameInProgress={documentRenameInProgress}
        renamePopupVisible={renamePopupVisible}
        documentName={documentName}
        selectedDocumentToRename={selectedDocumentToRename}
        nameError={nameError}
        errors={errors}
      />
      {content}
      <div id="delete-popup-wrapper" />
    </div>
  );
};

export default CustomerDocuments;
